import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/** Joins class names and lets later Tailwind classes win over earlier ones. */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** The host of a URL without `www.`, or the input when it is not a URL. */
export function domainOf(url: string | null | undefined): string {
  if (!url) {
    return "";
  }
  try {
    return new URL(url, window.location.origin).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** Reads a value from localStorage without throwing when storage is blocked. */
export function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Writes a value to localStorage without throwing when storage is blocked. */
export function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    // The value only lives for this page load.
  }
}

/**
 * Opens a stored file in a new tab.
 * Browsers refuse top-level navigation to `data:` URLs, so those are turned into a blob URL first.
 */
export function openFile(url: string): void {
  if (!url.startsWith("data:")) {
    window.open(url, "_blank", "noopener");
    return;
  }
  void fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener");
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    });
}

/** Pretty-prints JSON values; strings that hold JSON are parsed first. */
export function prettyJson(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
      try {
        return JSON.stringify(JSON.parse(trimmed), null, 2);
      } catch {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value, null, 2) ?? String(value);
  } catch {
    return String(value);
  }
}

/** True when the string parses as a JSON object or array. */
export function isJsonText(value: string | null | undefined): boolean {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
    return false;
  }
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
}

/** Milliseconds between two ISO dates, or from the first one to `now`. */
export function elapsedMs(startIso: string | null | undefined, endIso: string | null | undefined, now: number): number | null {
  if (!startIso) {
    return null;
  }
  const start = Date.parse(startIso);
  const end = endIso ? Date.parse(endIso) : now;
  if (Number.isNaN(start) || Number.isNaN(end)) {
    return null;
  }
  return Math.max(0, end - start);
}

/** The current month as `YYYY-MM` in local time. */
export function currentMonth(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

/** Moves a `YYYY-MM` month by a number of months. */
export function shiftMonth(month: string, delta: number): string {
  const [year, monthIndex] = month.split("-").map(Number);
  const date = new Date(year ?? 2000, (monthIndex ?? 1) - 1 + delta, 1);
  return currentMonth(date);
}

/** Copies text to the clipboard; resolves false when the browser refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.append(area);
    area.select();
    const ok = document.execCommand("copy");
    area.remove();
    return ok;
  }
}
